import { useState } from "react";
import { toast } from "sonner";
import { Copy, KeyRound } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { api, errMsg } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { Field, useBusy } from "../common";
import { copyText, PreviewWarning } from "./leadUtils";
import { LeadSetupHelp } from "./LeadSetupHelp";

export function LeadSourceDialog({ source, providers, team, onClose, onSaved }) {
  const { t, lang } = useLang();
  const edit = !!source.id;
  const prov = providers.find((p) => p.key === source.type) || {};
  const [f, setF] = useState({ type: source.type, name: source.name || prov.name_en || "", default_service: source.default_service || "", assign_to: source.assign_to || "", redirect_url: source.redirect_url || "" });
  const [busy, run] = useBusy();
  const ch = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = (e) => run(async () => {
    e.preventDefault();
    try {
      const body = { ...f, assign_to: f.assign_to || null };
      const { data } = edit ? await api.put(`/lead-sources/${source.id}`, body) : await api.post("/lead-sources", body);
      toast.success(t("Lead source saved", "Fuente guardada"));
      onSaved(data);
    } catch (err) { toast.error(errMsg(err)); }
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="lead-source-dialog">
        <DialogHeader>
          <DialogTitle>{edit ? t("Edit lead source", "Editar fuente") : `${t("Connect", "Conectar")} ${lang === "es" ? prov.name_es : prov.name_en}`}</DialogTitle>
          {!edit && <DialogDescription>{t("A private webhook URL and secret key will be created only for your company.", "Se creará una URL de webhook y una clave secreta privadas solo para su empresa.")}</DialogDescription>}
        </DialogHeader>
        <form onSubmit={save} className="grid gap-3" data-testid={`lead-source-form-${f.type}`}>
          <Field label={t("Name", "Nombre")}><input className="inp" required maxLength={80} value={f.name} onChange={ch("name")} placeholder={t("e.g. Google Ads – Stucco campaign", "ej. Google Ads – Campaña estuco")} data-testid="lead-source-name" /></Field>
          <Field label={t("Default service (optional)", "Servicio predeterminado (opcional)")}><input className="inp" maxLength={120} value={f.default_service} onChange={ch("default_service")} data-testid="lead-source-service" /></Field>
          <Field label={t("Assign new leads to", "Asignar prospectos a")}>
            <select className="inp" value={f.assign_to} onChange={ch("assign_to")} data-testid="lead-source-assign">
              <option value="">{t("Nobody (team decides)", "Nadie (el equipo decide)")}</option>
              {team.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </Field>
          {f.type === "website" && <Field label={t("Thank-you page URL (optional, https://)", "Página de agradecimiento (opcional, https://)")}><input className="inp" value={f.redirect_url} onChange={ch("redirect_url")} placeholder="https://hcalixpainting.com/thank-you" data-testid="lead-source-redirect" /></Field>}
          <button className="btn-primary" disabled={busy} data-testid="lead-source-save-btn">{edit ? t("Save", "Guardar") : t("Connect", "Conectar")}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function LeadKeyDialog({ source, secret, onClose }) {
  const { t } = useLang();
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="lead-key-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5" /> {source.name}</DialogTitle>
          <DialogDescription>{t("Copy the secret key now. For your security it will not be shown again (you can generate a new one anytime).", "Copie la clave secreta ahora. Por seguridad no se volverá a mostrar (puede generar una nueva cuando quiera).")}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          {source.is_preview && <PreviewWarning t={t} />}
          {[["url", t("Webhook URL", "URL del webhook"), source.webhook_url], ["key", t("Secret key", "Clave secreta"), secret]].map(([k, l, v]) => (
            <Field key={k} label={l}>
              <div className="flex gap-2"><input className="inp mono text-xs" readOnly value={v} data-testid={`lead-key-${k}`} /><button className="btn-outline btn-sm" onClick={() => copyText(v, t)} data-testid={`lead-key-${k}-copy`}><Copy className="w-4 h-4" /></button></div>
            </Field>
          ))}
          <LeadSetupHelp source={source} secret={secret} />
          <button className="btn-dark" onClick={onClose} data-testid="lead-key-done-btn">{t("I saved the key", "Ya guardé la clave")}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
